import React, { useEffect, useRef, useState } from "react";
import { Users, CalendarClock, Receipt, ArrowLeftRight, BarChart3 } from "lucide-react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useSpring,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import DeviceFrame from "../common/DeviceFrame";
import Reveal from "../common/Reveal";
import EmiSimulator from "./EmiSimulator";
import { SCREENS } from "../../content/product";

const EASE = [0.22, 1, 0.36, 1];

const STEPS = [
  {
    icon: Users,
    tag: "Customers",
    title: "Manage your customers with clarity.",
    body:
      "Open a borrower record in seconds — name, mobile, email and PIN — and keep every loan tied to the person who holds it. One directory instead of a shelf of registers.",
    image: SCREENS.register,
    alt: "EMIBOOK customer registration screen",
    accent: "#2563eb",
  },
  {
    icon: CalendarClock,
    tag: "EMI",
    title: "Keep every EMI organized.",
    body:
      "Each loan sits in its own entry with the amount and the installments due, so a borrower's schedule reads the same way every time you open it.",
    image: SCREENS.orders,
    alt: "EMIBOOK orders list showing EMI entries",
    accent: "#4f46e5",
  },
  {
    icon: Receipt,
    tag: "Payments",
    title: "Track payments effortlessly.",
    body:
      "Record a collection with the amount, the transaction number and the payment screenshot attached. The receipt is captured at the moment of payment, not reconstructed later.",
    image: SCREENS.payment,
    alt: "EMIBOOK payment entry screen",
    accent: "#7c3aed",
  },
  {
    icon: ArrowLeftRight,
    tag: "Transactions",
    title: "Keep every transaction organized.",
    body:
      "Settled entries stay in the log with their date, value and status — a running history you can scroll back through instead of a drawer of paper slips.",
    image: SCREENS.orderRecord,
    alt: "EMIBOOK transaction record",
    accent: "#0891b2",
  },
  {
    icon: BarChart3,
    tag: "Insights",
    title: "Understand your business better.",
    body:
      "The home screen carries your live notifications and direct routes into loans and customers — the state of the book, the moment you open the app.",
    image: SCREENS.dashboard,
    alt: "EMIBOOK dashboard overview",
    accent: "#059669",
  },
];

export default function ScrollStory() {
  const trackRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);

  // Progress across the whole story track drives which step is showing.
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start 55%", "end 85%"],
  });

  const smooth = useSpring(scrollYProgress, {
    stiffness: 60,
    damping: 24,
    restDelta: 0.0005,
  });

  // A gentle drift on the sticky visual keeps the column feeling connected
  // to the scroll rather than frozen.
  const visualY = useTransform(smooth, [0, 1], [16, -16]);
  const railScale = useTransform(smooth, [0, 1], [0, 1]);

  useEffect(() => {
    const unsubscribe = smooth.onChange((v) => {
      const clamped = Math.min(0.9999, Math.max(0, v));
      const next = Math.floor(clamped * STEPS.length);
      setActive((prev) => (prev === next ? prev : next));
    });
    return unsubscribe;
  }, [smooth]);

  const current = STEPS[active] || STEPS[0];

  return (
    <section id="how-it-works" className="eb-bg-surface eb-section">
      <div className="container">
        <Reveal className="row" style={{ marginBottom: "clamp(48px, 6vw, 76px)" }}>
          <div className="col-lg-7">
            <span className="eb-eyebrow">How it works</span>
            <h2 className="eb-section-title">
              One continuous workflow,
              <br />
              from customer to closing entry.
            </h2>
            <p className="eb-subtitle" style={{ marginTop: "18px", marginBottom: 0 }}>
              Keep scrolling — the screen on the right follows along.
            </p>
          </div>
        </Reveal>

        <div ref={trackRef} className="row">
          {/* ------------------------------------------------------------ */}
          {/* Left: the steps                                              */}
          {/* ------------------------------------------------------------ */}
          <div className="col-lg-6 order-2 order-lg-1">
            <div style={{ position: "relative" }}>
              {/* Progress rail that fills as the story advances */}
              {!reduceMotion && (
                <motion.div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "2px",
                    height: "100%",
                    background: "var(--color-primary)",
                    transformOrigin: "top center",
                    scaleY: railScale,
                    opacity: 0.5,
                    zIndex: 1,
                  }}
                />
              )}

              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                const isActive = idx === active;
                return (
                  <div
                    key={step.tag}
                    className={`eb-story-step ${isActive ? "is-active" : ""}`}
                    style={{
                      // Tall enough that each step owns a real stretch of
                      // scroll — the transitions should feel scrubbed, not
                      // flicked through.
                      minHeight: "clamp(300px, 58vh, 500px)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "18px" }}>
                      <span
                        aria-hidden="true"
                        style={{
                          fontFamily: "var(--font-display)",
                          fontWeight: 700,
                          fontSize: "clamp(2.6rem, 4.2vw, 3.6rem)",
                          lineHeight: 1,
                          letterSpacing: "-0.03em",
                          color: step.accent,
                          opacity: isActive ? 0.16 : 0.08,
                          transition: "opacity 0.5s var(--ease-out-soft)",
                          flexShrink: 0,
                        }}
                      >
                        {String(idx + 1).padStart(2, "0")}
                      </span>

                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "14px",
                          }}
                        >
                          <span
                            className="eb-icon-tile"
                            style={{
                              width: "34px",
                              height: "34px",
                              borderRadius: "10px",
                              background: `${step.accent}14`,
                              color: step.accent,
                            }}
                          >
                            <Icon size={17} />
                          </span>
                          <span
                            style={{
                              fontFamily: "var(--font-display)",
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                              color: step.accent,
                            }}
                          >
                            {step.tag}
                          </span>
                        </div>

                        <h3
                          style={{
                            fontFamily: "var(--font-display)",
                            fontSize: "clamp(1.35rem, 2.1vw, 1.7rem)",
                            fontWeight: 700,
                            letterSpacing: "-0.024em",
                            lineHeight: 1.22,
                            color: "var(--text-main)",
                            marginBottom: "14px",
                          }}
                        >
                          {step.title}
                        </h3>
                      </div>
                    </div>

                    <p
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "1rem",
                        lineHeight: 1.68,
                        maxWidth: "460px",
                        margin: 0,
                      }}
                    >
                      {step.body}
                    </p>

                    {/* Mobile: the screen sits inside its own step */}
                    <div className="d-lg-none" style={{ marginTop: "28px" }}>
                      <DeviceFrame src={step.image} alt={step.alt} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ------------------------------------------------------------ */}
          {/* Right: the sticky product visual                             */}
          {/* ------------------------------------------------------------ */}
          <div className="col-lg-6 order-1 order-lg-2 d-none d-lg-block">
            <div className="eb-sticky-col">
              <motion.div
                style={{
                  position: "relative",
                  y: reduceMotion ? 0 : visualY,
                }}
              >
                {/* Accent wash tinted by the active step */}
                <motion.div
                  aria-hidden="true"
                  animate={{ background: `${current.accent}12` }}
                  transition={{ duration: 0.8, ease: EASE }}
                  style={{
                    position: "absolute",
                    inset: "-14% -22%",
                    borderRadius: "50%",
                    filter: "blur(52px)",
                    zIndex: 0,
                  }}
                />

                {/* Sized from the viewport height so the pinned phone always
                    fits between the header and the fold. */}
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    height: "min(60vh, 500px)",
                    aspectRatio: "9 / 19.5",
                    margin: "0 auto",
                  }}
                >
                  <AnimatePresence initial={false}>
                    <motion.div
                      key={active}
                      initial={
                        reduceMotion
                          ? { opacity: 1 }
                          : { opacity: 0, scale: 0.965, filter: "blur(5px)" }
                      }
                      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                      exit={
                        reduceMotion
                          ? { opacity: 0 }
                          : { opacity: 0, scale: 1.02, filter: "blur(5px)" }
                      }
                      transition={{ duration: 0.62, ease: EASE }}
                      style={{ position: "absolute", inset: 0 }}
                    >
                      <DeviceFrame
                        src={current.image}
                        alt={current.alt}
                        style={{ width: "100%", height: "100%", maxWidth: "none" }}
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Step indicator under the phone */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "7px",
                    marginTop: "26px",
                  }}
                >
                  {STEPS.map((s, idx) => (
                    <motion.span
                      key={s.tag}
                      aria-hidden="true"
                      animate={{
                        width: idx === active ? 22 : 6,
                        backgroundColor:
                          idx === active ? current.accent : "#cbd5e1",
                      }}
                      transition={{ duration: 0.45, ease: EASE }}
                      style={{ height: 6, borderRadius: 3, display: "block" }}
                    />
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Interactive calculator — every figure is computed from the inputs */}
        <Reveal style={{ marginTop: "clamp(64px, 8vw, 104px)" }}>
          <EmiSimulator />
        </Reveal>
      </div>
    </section>
  );
}
